import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from 'components/ui';
import { useFormLocked } from 'components/ui/FormLock';
import styles from './SclaStep.module.scss';

const MIN_STROKE_LENGTH = 30;
const MIN_POINT_COUNT = 8;

type InkPoint = { x: number; y: number; time?: number; color?: string };

type StrokePoint = { clientX: number; clientY: number };

type StrokePad = {
  off: () => void;
};

const STROKE = {
  begin: '_strokeBegin',
  move: '_strokeMoveUpdate',
  end: '_strokeEnd',
} as const;

function stroke(pad: StrokePad, name: keyof typeof STROKE, point: StrokePoint) {
  const key = STROKE[name];
  const target = pad as unknown as Record<string, (event: StrokePoint) => void>;
  target[key](point);
}

type PadHandle = {
  toData: () => InkPoint[][];
  fromData: (groups: InkPoint[][]) => void;
  getCanvas: () => HTMLCanvasElement;
  getSignaturePad: () => StrokePad;
  clear: () => void;
};

function padHandle(pad: SignatureCanvas | null): PadHandle | null {
  return pad as unknown as PadHandle | null;
}

function inkSize(pad: PadHandle): { points: number; length: number } {
  const groups = pad.toData() as Array<InkPoint[] | { points?: InkPoint[] }>;
  let points = 0;
  let length = 0;
  groups.forEach(group => {
    const marks = Array.isArray(group) ? group : group.points || [];
    points += marks.length;
    for (let index = 1; index < marks.length; index += 1) {
      const dx = marks[index].x - marks[index - 1].x;
      const dy = marks[index].y - marks[index - 1].y;
      length += Math.hypot(dx, dy);
    }
  });
  return { points, length };
}

interface ISclaSignaturePadProps {
  id: string;
  label: string;
  disabled?: boolean;
  onSigned: (pad: SignatureCanvas) => void;
  onClear: () => Promise<void>;
}

export default function SclaSignaturePad({
  id,
  label,
  disabled = false,
  onSigned,
  onClear,
}: ISclaSignaturePadProps) {
  const locked = useFormLocked();
  const isDisabled = disabled || locked;
  const boxRef = useRef<HTMLDivElement>(null);
  const padRef = useRef<SignatureCanvas>(null);
  const drawing = useRef<number | null>(null);
  const cssSize = useRef({ width: 0, height: 0 });
  const [tooShort, setTooShort] = useState(false);

  useEffect(() => {
    const box = boxRef.current;
    const pad = padRef.current;
    if (!box || !pad) return undefined;

    const fit = () => {
      const { current } = padRef;
      const frame = boxRef.current;
      if (!current || !frame) return;
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const width = frame.clientWidth;
      const height = frame.clientHeight;
      if (width < 2 || height < 2) return;
      const handle = padHandle(current);
      if (!handle) return;
      const canvas = handle.getCanvas();
      const previous = cssSize.current;
      const data = handle.toData();
      const scaleX = previous.width ? width / previous.width : 1;
      const scaleY = previous.height ? height / previous.height : 1;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.getContext('2d')?.setTransform(ratio, 0, 0, ratio, 0, 0);
      if (data.length) {
        const scaled = data.map(group =>
          group.map(point => ({
            ...point,
            x: point.x * scaleX,
            y: point.y * scaleY,
          })),
        );
        handle.fromData(scaled);
      }
      handle.getSignaturePad().off();
      cssSize.current = { width, height };
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(box);
    return () => observer.disconnect();
  }, []);

  const strokePad = () => padHandle(padRef.current)?.getSignaturePad();

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (isDisabled) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const pad = strokePad();
    if (!pad) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = event.pointerId;
    stroke(pad, 'begin', { clientX: event.clientX, clientY: event.clientY });
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (drawing.current !== event.pointerId) return;
    const pad = strokePad();
    if (!pad) return;
    stroke(pad, 'move', { clientX: event.clientX, clientY: event.clientY });
  };

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (drawing.current !== event.pointerId) return;
    drawing.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (isDisabled) return;
    const node = padRef.current;
    const api = padHandle(node);
    const pad = strokePad();
    if (!node || !api || !pad) return;
    stroke(pad, 'end', { clientX: event.clientX, clientY: event.clientY });
    const size = inkSize(api);
    if (size.length < MIN_STROKE_LENGTH && size.points < MIN_POINT_COUNT) {
      setTooShort(true);
      return;
    }
    setTooShort(false);
    onSigned(node);
  };

  const clear = async () => {
    if (isDisabled) return;
    await onClear();
    padHandle(padRef.current)?.clear();
    setTooShort(false);
  };

  return (
    <div className={isDisabled ? `${styles.pad} ${styles.padOff}` : styles.pad}>
      <div className={styles.sigBox} ref={boxRef} id={id}>
        <SignatureCanvas
          ref={padRef}
          penColor="black"
          clearOnResize={false}
          canvasProps={{
            className: 'scla-signature',
            'aria-label': label,
            style: { touchAction: 'none', width: '100%', height: '100%' },
            onPointerDown,
            onPointerMove,
            onPointerUp,
            onPointerCancel: onPointerUp,
          }}
        />
      </div>
      {tooShort ? (
        <p className={styles.fieldError} role="alert">Please sign in the box.</p>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        disabled={isDisabled}
        onClick={() => {
          clear().catch(() => undefined);
        }}
      >
        Clear
      </Button>
    </div>
  );
}
